import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Search,
  Building2,
  MapPin,
  Calendar,
  Globe,
  FileText,
  Video,
  ExternalLink,
  Tag,
  RefreshCw,
} from 'lucide-react';
import { fetchPortalExhibitors, PortalExhibitor } from '../../lib/api';

interface PortalExhibitorDirectoryProps {
  onScheduleMeeting: (exhibitor: PortalExhibitor) => void;
}

const CATEGORY_FILTERS = [
  'All Categories',
  'Agrochemicals',
  'Biostimulants & Biofertilizers',
  'Formulations & Active Ingredients',
  'Agricultural Machinery & Implements',
  'Precision Irrigation & Water Tech',
  'Drone & Robotics Tech',
  'Seeds & Hybrid Genetics',
  'Crop Protection & Nutrition',
  'Agri-Fintech & Digital Supply Chain',
  'Post-Harvest Packaging & Cold Chain',
];

export function PortalExhibitorDirectory({ onScheduleMeeting }: PortalExhibitorDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

  const { data, isLoading } = useQuery({
    queryKey: ['portal-exhibitors', searchQuery, selectedCategory],
    queryFn: () =>
      fetchPortalExhibitors({
        category: selectedCategory !== 'All Categories' ? selectedCategory : undefined,
        search: searchQuery.trim() || undefined,
      }),
  });

  const exhibitors = data?.exhibitors || [];

  return (
    <div className="space-y-6">
      {/* Search & Category Filter */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search exhibitors by company, booth (e.g. A-101), or product..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 focus:outline-none focus:border-[#0A3D27]"
          >
            {CATEGORY_FILTERS.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Exhibitor Grid */}
      {isLoading ? (
        <div className="text-center py-16">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#0A3D27]" />
          <p className="text-xs text-slate-500 mt-2">Loading exhibitor directory...</p>
        </div>
      ) : exhibitors.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 p-8 space-y-2">
          <Building2 className="w-10 h-10 text-slate-400 mx-auto" />
          <h3 className="text-sm font-bold text-slate-900">No exhibitors found</h3>
          <p className="text-xs text-slate-500">Try adjusting your keyword search or category filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exhibitors.map((ex) => (
            <div
              key={ex.reservationId}
              className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 text-[#0A3D27] flex items-center justify-center font-bold text-base overflow-hidden">
                      {ex.directoryLogoUrl ? (
                        <img src={ex.directoryLogoUrl} alt={ex.companyName} className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 leading-snug">
                        {ex.fasciaName || ex.companyName}
                      </h4>
                      <p className="text-xs text-slate-500 font-medium">
                        {ex.companyName}
                      </p>
                    </div>
                  </div>

                  {/* Stall Badge */}
                  <div className="px-2.5 py-1 rounded-xl bg-purple-50 border border-purple-200 text-purple-900 font-mono font-bold text-xs flex items-center gap-1 shrink-0">
                    <MapPin className="w-3 h-3 text-purple-600" />
                    <span>Stall {ex.boothId}</span>
                  </div>
                </div>

                {ex.directoryBio && (
                  <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed">
                    {ex.directoryBio}
                  </p>
                )}

                {/* Categories */}
                {ex.directoryCategories && ex.directoryCategories.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {ex.directoryCategories.map((cat, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-slate-100 text-[10px] text-slate-600 font-medium flex items-center gap-1"
                      >
                        <Tag className="w-2.5 h-2.5 text-slate-400" />
                        <span>{cat}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom Actions */}
              <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-xs">
                  {ex.directoryWebsite && (
                    <a
                      href={ex.directoryWebsite.startsWith('http') ? ex.directoryWebsite : `https://${ex.directoryWebsite}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-slate-500 hover:text-[#0A3D27] flex items-center gap-1"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      <span>Website</span>
                    </a>
                  )}

                  <Link
                    to={`/floorplan?highlight=${encodeURIComponent(ex.boothId)}`}
                    className="font-medium text-emerald-800 hover:text-emerald-950 flex items-center gap-1"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    <span>View Floorplan</span>
                  </Link>
                </div>

                <button
                  onClick={() => onScheduleMeeting(ex)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold shadow-xs transition-all cursor-pointer"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Book Meeting</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PortalExhibitorDirectory;
